import { matchesNameQuery } from '../../../utils/name-search';
import { ReadState } from '../../_shared/readState';

/**
 * Medal colors for top 3 ranks.
 */
export const RANK_MEDAL_COLORS: Record<number, string> = {
  1: '#FFD700', // Gold
  2: '#C0C0C0', // Silver
  3: '#CD7F32', // Bronze
};

/**
 * Returns the color for a win rate value.
 * >= 0.6 = green, >= 0.4 = yellow, < 0.4 = red
 */
export function getWinRateColor(rate: number): 'green' | 'yellow' | 'red' {
  if (rate >= 0.6) return 'green';
  if (rate >= 0.4) return 'yellow';
  return 'red';
}

/**
 * Filters entries by name (case-insensitive substring search on firstName + lastName).
 */
export function filterByName<T extends { user: { firstName?: string; lastName?: string } }>(
  entries: T[],
  query: string,
): T[] {
  if (!query) return entries;
  return entries.filter((e) => matchesNameQuery(query, e.user));
}

/** What the leaderboard body renders, decided in one place. */
export type LeaderboardView =
  | 'scope-organization'
  | 'scope-location'
  | 'loading'
  | 'unreadable'
  | 'answered';

/**
 * Which of the five the page should render, from the two reads behind it.
 *
 * This exists as a function, rather than as a ternary chain in the page,
 * because `useGetLeaderboard` is the first surface in this campaign whose gate
 * has a term the page's own scope does not name:
 *
 *     enabled: Boolean(organizationId) && Boolean(sport)
 *
 * `sport` is `availableSports[0]`, and `availableSports` is derived from
 * `useGetCourts(selectedLocation?.id)` — the LOCATION's courts. So the
 * leaderboard read is never issued in three different situations, and only the
 * first is the one this campaign usually finds:
 *
 *   no organization   the usual shape. Prompt for the organization.
 *   no location       the courts read has no location to ask about, so there
 *                     is no sport, so the leaderboard is disabled — with an
 *                     organization selected and nothing on the page saying so.
 *                     Prompt for the location.
 *   no sport          both scopes chosen and the location's courts declare no
 *                     sport. The read CANNOT be issued and never will be, and
 *                     here — and only here — the page's own empty state is a
 *                     true sentence: there is nothing to rank.
 *
 * The third arm is the one to be careful about, and it is why the courts read's
 * own state is a parameter. `courts === undefined` means "in flight", "refused"
 * or "not asked" just as much as it means "this club has no courts", so
 * reaching the empty claim requires the courts read to have ANSWERED. A refused
 * courts read makes the leaderboard unreadable rather than empty — we do not
 * know what sports exist, so we cannot say there is nothing to rank. That is
 * R105-1's trap (`TimeWindowGroupList`) one layer out: the defect is not the
 * permanent affordance, it is the confident sentence the obvious repair
 * manufactures out of it.
 */
export function leaderboardReadView(input: {
  leaderboard: ReadState;
  courts: ReadState;
  hasOrganization: boolean;
  hasLocation: boolean;
}): LeaderboardView {
  const { leaderboard, courts, hasOrganization, hasLocation } = input;

  if (leaderboard === 'unreadable') return 'unreadable';
  if (leaderboard === 'loading') return 'loading';
  if (leaderboard === 'answered') return 'answered';

  // never-issued: which of the two gate terms is unsatisfied?
  if (!hasOrganization) return 'scope-organization';
  if (!hasLocation) return 'scope-location';

  // Both scopes are chosen, so the missing term is the sport — and what we can
  // honestly say about it is a property of the COURTS read, not of this one.
  if (courts === 'unreadable') return 'unreadable';
  if (courts === 'answered') return 'answered';
  return 'loading';
}
